import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MapIcon from '@mui/icons-material/Map';
import { mainApi } from '../../../../config/apiClient';
import { useAuth } from '../../../../contexts/AuthContext';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Leaflet default icon fix
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Harita container styled component
const MapModalContainer = styled(Box)(({ theme }) => ({
  height: '400px',
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '& .leaflet-container': {
    height: '100%',
    width: '100%',
  },
}));

// Kurye tipi
interface Courier {
  id: number;
  firebaseUid: string;
  name: string;
  email: string;
  phone: string;
  status: 'active' | 'busy' | 'offline';
  currentLocation: string;
  branchIds: string;
  branchNames: string;
  totalDeliveries: number;
  completedToday: number;
  rating: number;
  lastLatitude?: number;
  lastLongitude?: number;
  lastLocationUpdate?: string;
}

interface SelectCourierModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCourier: (courierId: number, courierFirebaseUid: string, courierName: string) => void;
  companyId: string;
  branchId?: string | null;
  orderInfo?: {
    orderId: number;
    customerName: string;
    address: string;
  };
}

const SelectCourierModal: React.FC<SelectCourierModalProps> = ({
  open,
  onClose,
  onSelectCourier,
  companyId,
  branchId,
  orderInfo,
}) => {
  const { branches } = useAuth(); // Şube bilgilerini al
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourierId, setSelectedCourierId] = useState<string | null>(null);
  
  // Harita modalı için state'ler
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [courierForLocation, setCourierForLocation] = useState<Courier | null>(null);

  // Kuryeleri yükle
  useEffect(() => {
    if (!open || !companyId) return;

    const loadCouriers = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('🔍 [SelectCourier] Kuryeler yükleniyor...', { companyId, branchId });

        const response = await mainApi.get(`/api/Couriers`);

        if (response.data.success) {
          const apiCouriers = response.data.data || [];
          console.log('📦 [SelectCourier] API Response:', apiCouriers);

          // Kuryeleri filtrele - şubeye göre
          let filteredCouriers = apiCouriers;

          if (branchId) {
            filteredCouriers = apiCouriers.filter((c: any) => {
              // Sube atanmamis kuryeler tum subelerde gorunur
              if (!c.branchIds) return true;
              
              let branchIdArray: string[] = [];
              if (typeof c.branchIds === 'string') {
                try {
                  const parsed = JSON.parse(c.branchIds);
                  branchIdArray = Array.isArray(parsed) ? parsed : [c.branchIds];
                } catch {
                  branchIdArray = c.branchIds.split(',').map((id: string) => id.trim());
                }
              } else if (Array.isArray(c.branchIds)) {
                branchIdArray = c.branchIds;
              }

              // Bos branchIds → tum subelere ait
              if (branchIdArray.length === 0) return true;

              return branchIdArray.includes(branchId);
            });
            console.log(`📍 [SelectCourier] Şube ${branchId} için ${filteredCouriers.length} kurye bulundu`);
          } else {
            // Şube yoksa, şubesi olmayan kuryeleri getir
            filteredCouriers = apiCouriers.filter((c: any) => {
              if (!c.branchIds) return true;
              
              let branchIdArray: string[] = [];
              if (typeof c.branchIds === 'string') {
                try {
                  const parsed = JSON.parse(c.branchIds);
                  branchIdArray = Array.isArray(parsed) ? parsed : [c.branchIds];
                } catch {
                  branchIdArray = c.branchIds.split(',').filter((id: string) => id.trim());
                }
              } else if (Array.isArray(c.branchIds)) {
                branchIdArray = c.branchIds;
              }

              return branchIdArray.length === 0;
            });
            console.log(`📍 [SelectCourier] Şubesiz ${filteredCouriers.length} kurye bulundu`);
          }

          // Kuryeleri frontend tipine dönüştür
          const courierList: Courier[] = filteredCouriers.map((c: any) => {
            // Şube ID'lerini şube isimlerine çevir
            let branchNamesStr = 'Şube Atanmamış';
            let branchIdsStr = '';
            
            if (c.branchIds && typeof c.branchIds === 'string') {
              branchIdsStr = c.branchIds;
              try {
                // JSON array olarak parse etmeyi dene
                const parsed = JSON.parse(c.branchIds);
                if (Array.isArray(parsed)) {
                  const branchNameArray = parsed.map((branchIdItem: string) => {
                    const branch = branches?.find((b: any) => b.id === branchIdItem?.trim?.());
                    return branch?.name || 'Bilinmeyen Şube';
                  });
                  branchNamesStr = branchNameArray.join(', ') || 'Şube Atanmamış';
                }
              } catch {
                // JSON parse başarısız, virgülle ayır
                const branchIdArray = c.branchIds.split(',').filter((id: string) => id.trim());
                const branchNameArray = branchIdArray.map((branchIdItem: string) => {
                  const branch = branches?.find((b: any) => b.id === branchIdItem.trim());
                  return branch?.name || 'Bilinmeyen Şube';
                });
                branchNamesStr = branchNameArray.join(', ') || 'Şube Atanmamış';
              }
            } else if (Array.isArray(c.branchIds)) {
              branchIdsStr = c.branchIds.join(',');
              const branchNameArray = c.branchIds.map((branchIdItem: string) => {
                const branch = branches?.find((b: any) => b.id === branchIdItem?.trim?.());
                return branch?.name || 'Bilinmeyen Şube';
              });
              branchNamesStr = branchNameArray.join(', ') || 'Şube Atanmamış';
            }
            
            return {
              id: c.id,
              firebaseUid: c.firebaseUid,
              name: c.name || 'İsimsiz Kurye',
              email: c.email || '',
              phone: c.phoneNumber || '',
              status: c.status === 'active' ? 'active' :
                      c.status === 'busy' ? 'busy' : 'offline',
              currentLocation: c.currentLocationText || 'Konum bilgisi yok',
              branchIds: branchIdsStr,
              branchNames: branchNamesStr,
              totalDeliveries: c.totalDeliveries || 0,
              completedToday: c.completedToday || 0,
              rating: c.rating || 5.0,
              lastLatitude: c.lastLatitude,
              lastLongitude: c.lastLongitude,
              lastLocationUpdate: c.lastLocationUpdate,
            };
          });

          // Aktif olanları öne al
          courierList.sort((a, b) => {
            if (a.status === 'active' && b.status !== 'active') return -1;
            if (a.status !== 'active' && b.status === 'active') return 1;
            return a.name.localeCompare(b.name);
          });

          console.log('✅ [SelectCourier] Kuryeler yüklendi:', courierList.length);
          setCouriers(courierList);
        } else {
          throw new Error(response.data.message || 'Kuryeler yüklenemedi');
        }
      } catch (error) {
        console.error('❌ [SelectCourier] Kurye yükleme hatası:', error);
        setError('Kurye verileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadCouriers();
  }, [open, companyId, branchId, branches]);

  // Durum rengini belirle
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'busy':
        return 'warning';
      case 'offline':
      default:
        return 'default';
    }
  };

  // Durum metnini belirle
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'busy':
        return 'Meşgul';
      case 'offline':
      default:
        return 'Çevrimdışı';
    }
  };

  // Kurye seçimi
  const handleSelectCourier = () => {
    const selectedCourier = couriers.find(c => c.firebaseUid === selectedCourierId);
    if (selectedCourier) {
      console.log('✅ [SelectCourier] Kurye seçildi:', selectedCourier.id, selectedCourier.name);
      onSelectCourier(selectedCourier.id, selectedCourier.firebaseUid, selectedCourier.name);
      onClose();
    }
  };

  // Konum harita modalı aç
  const handleLocationClick = (e: React.MouseEvent, courier: Courier) => {
    e.stopPropagation(); // Satır seçimini engelle
    if (courier.lastLatitude && courier.lastLongitude) {
      setCourierForLocation(courier);
      setLocationModalOpen(true);
    }
  };

  // Harita modalını kapat
  const handleCloseLocationModal = () => {
    setLocationModalOpen(false);
    setCourierForLocation(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white',
        py: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon />
          <Typography variant="h6">Kurye Seç</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Sipariş bilgisi */}
        {orderInfo && (
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Sipariş #{orderInfo.orderId}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {orderInfo.customerName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {orderInfo.address}
            </Typography>
          </Box>
        )}

        {/* Yükleniyor */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Hata */}
        {error && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {/* Kurye listesi */}
        {!loading && !error && (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Kurye</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Konum</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>İletişim</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100', textAlign: 'center' }}>Bugün</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {couriers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {branchId 
                          ? 'Bu şubeye atanmış kurye bulunamadı' 
                          : 'Uygun kurye bulunamadı'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  couriers.map((courier) => (
                    <TableRow
                      key={courier.firebaseUid}
                      hover
                      selected={selectedCourierId === courier.firebaseUid}
                      onClick={() => setSelectedCourierId(courier.firebaseUid)}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: selectedCourierId === courier.firebaseUid ? 'primary.light' : 'inherit',
                        '&:hover': {
                          bgcolor: selectedCourierId === courier.firebaseUid ? 'primary.light' : 'action.hover'
                        }
                      }}
                    >
                      {/* Kurye Adı */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                            {courier.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {courier.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {courier.branchNames}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Durum */}
                      <TableCell>
                        <Chip
                          label={getStatusLabel(courier.status)}
                          color={getStatusColor(courier.status) as any}
                          size="small"
                          sx={{ minWidth: 80 }}
                        />
                      </TableCell>

                      {/* Konum */}
                      <TableCell>
                        <Tooltip title={courier.lastLatitude && courier.lastLongitude ? "Haritada göster" : "Konum bilgisi yok"}>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              cursor: courier.lastLatitude && courier.lastLongitude ? 'pointer' : 'default',
                              '&:hover': courier.lastLatitude && courier.lastLongitude ? {
                                color: 'primary.main',
                                '& .MuiSvgIcon-root': { color: 'primary.main' }
                              } : {}
                            }}
                            onClick={(e) => handleLocationClick(e, courier)}
                          >
                            <LocationOnIcon sx={{ 
                              fontSize: 16, 
                              color: courier.lastLatitude && courier.lastLongitude ? 'success.main' : 'text.secondary' 
                            }} />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: 150, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                textDecoration: courier.lastLatitude && courier.lastLongitude ? 'underline' : 'none',
                              }}
                            >
                              {courier.currentLocation}
                            </Typography>
                            {courier.lastLatitude && courier.lastLongitude && (
                              <MapIcon sx={{ fontSize: 14, color: 'primary.main', ml: 0.5 }} />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>

                      {/* İletişim */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {courier.phone && (
                            <Tooltip title={`Ara: ${courier.phone}`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                <Typography variant="caption">{courier.phone}</Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {courier.email && (
                            <Tooltip title={courier.email}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: 'info.main' }} />
                                <Typography variant="caption" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {courier.email}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Bugünkü Teslimatlar */}
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {courier.completedToday}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          teslimat
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleSelectCourier}
          disabled={!selectedCourierId}
          startIcon={<LocalShippingIcon />}
        >
          Kurye Ata
        </Button>
      </DialogActions>

      {/* Kurye Konum Harita Modalı */}
      <Dialog
        open={locationModalOpen}
        onClose={handleCloseLocationModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'success.main',
          color: 'white',
          py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon />
            <Typography variant="h6">
              {courierForLocation?.name} - Konum
            </Typography>
          </Box>
          <IconButton onClick={handleCloseLocationModal} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          {courierForLocation?.lastLatitude && courierForLocation?.lastLongitude && (
            <MapModalContainer>
              <MapContainer
                center={[courierForLocation.lastLatitude, courierForLocation.lastLongitude]}
                zoom={15}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[courierForLocation.lastLatitude, courierForLocation.lastLongitude]}>
                  <Popup>
                    <Box sx={{ minWidth: 150 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {courierForLocation.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {courierForLocation.currentLocation}
                      </Typography>
                      {courierForLocation.lastLocationUpdate && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Son güncelleme: {new Date(courierForLocation.lastLocationUpdate).toLocaleString('tr-TR')}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Koordinat: {courierForLocation.lastLatitude.toFixed(6)}, {courierForLocation.lastLongitude.toFixed(6)}
                      </Typography>
                    </Box>
                  </Popup>
                </Marker>
              </MapContainer>
            </MapModalContainer>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseLocationModal} variant="contained">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SelectCourierModal;
